/**
 * Free Shipping Progress Bar
 * - Reads cart total (minor units) from data-cart-total
 * - Determines goal (minor units) from currency map or $100 USD fallback
 * - Uses Shopify.currency.rate to convert $100 -> active currency when possible
 * - Updates text/eligibility and animates the progress bar
 */

/* ----------------------------- Configuration ------------------------------ */
function parseShippingGoals(value) {
  const goals = {};
  let fallback = null;

  if (!value) {
    return { goals, fallback };
  }

  value.split(/\r?\n/).forEach((line) => {
    line = line.trim();

    if (!line) return;

    // Currency/country threshold: USD:100, EUR:80, BE:100
    if (line.includes(':')) {
      const [code, amount] = line.split(':');
      const key = code.trim().toUpperCase();
      const value = Number(amount.trim());

      if (key && Number.isFinite(value)) {
        goals[key] = value;
      }

      return;
    }

    // Standalone number = fallback
    const value = Number(line);

    if (Number.isFinite(value)) {
      fallback = value;
    }
  });

  return {
    goals,
    fallback
  };
}

// Currencies that don't use cents
const ZERO_DEC = new Set(['JPY', 'KRW', 'VND', 'CLP']);

/* --------------------------------- Utils ---------------------------------- */
function activeCurrency() {
  // Most reliable first
  if (window.Shopify && Shopify.currency && (Shopify.currency.active || Shopify.currency.current)) {
    return String(Shopify.currency.active || Shopify.currency.current).toUpperCase();
  }
  // Some themes store here
  if (window.theme && theme.currency) return String(theme.currency).toUpperCase();
  // Many themes/apps add data-currency to <html>
  const htmlCur = document.documentElement?.dataset?.currency;
  if (htmlCur) return String(htmlCur).toUpperCase();
  // Last resort
  return (window.shopCurrency || 'USD').toUpperCase();
}

function shopBaseCurrency() {
  if (window.Shopify && Shopify.currency) {
    return String(
      Shopify.currency.shopCurrency ||
      Shopify.currency.storeCurrency ||
      Shopify.currency.originCurrency ||
      Shopify.currency.base ||
      Shopify.currency.active ||
      'USD'
    ).toUpperCase();
  }

  return 'USD';
}

function toMinor(cur, major) {
  const n = Number(major);
  return ZERO_DEC.has(cur) ? Math.round(n) : Math.round(n * 100);
}

  function formatCents(cents) {
    var fmt =
      (window.theme &&
        theme.settings &&
        theme.settings.money_with_currency_format) ||
      (window.Shopify && Shopify.money_format) ||
      "${{amount}}";
    var fn = window.formatMoney || (window.Shopify && Shopify.formatMoney);
    if (typeof fn === "function") {
      try {
        return fn(cents, fmt);
      } catch (e) {}
    }
    return (cents / 100).toFixed(2); 
  }

/* --------------------------- Web Component --------------------------- */
if (!customElements.get('free-shipping-progress-bar')) {
  class FreeShippingProgressBar extends HTMLElement {
    constructor() {
      super();
      this._currentPercent = 0;
    }

    connectedCallback() {
      this.updateStatus();

      // Recalculate when attributes change (AJAX cart updates)
      this._mo = new MutationObserver((muts) => {
        for (const m of muts) {
          if (m.type === 'attributes' &&
              (m.attributeName === 'data-cart-total' || m.attributeName === 'data-shipping-amount')) {
            this.updateStatus();
            break;
          }
        }
      });
      this._mo.observe(this, { attributes: true, attributeFilter: ['data-cart-total', 'data-shipping-amount'] });
    }

    disconnectedCallback() {
      if (this._mo) this._mo.disconnect();
    }

    updateStatus() {
      const cartTotal = Number(this.dataset.cartTotal) || 0;

      const remainingBox = this.querySelector('.free-shipping__info-remaining');
      const remainingStrong = remainingBox
        ? remainingBox.querySelector('strong')
        : null;

      const eligibleBox = this.querySelector('.free-shipping__info-eligibility');

      const cur = activeCurrency();
      const country = this.dataset.country;

      const {
        goals,
        fallback
      } = parseShippingGoals(this.dataset.shippingGoals);

      // 1. Country-specific threshold has priority
      let goalMajor = country ? goals[country] : undefined;

      // 2. Then use currency-specific threshold
      if (!Number.isFinite(goalMajor)) {
        goalMajor = goals[cur];
      }

      // 3. Then use standalone fallback
      if (!Number.isFinite(goalMajor)) {
        goalMajor = fallback;
      }

      // 4. Finally use theme setting
      if (!Number.isFinite(goalMajor)) {
        goalMajor = Number(this.dataset.defaultGoal) || 50;
      }

      console.log('Country:', country);
      console.log('Currency:', cur);
      console.log('Goal major:', goalMajor);

      const goalMinor = toMinor(cur, goalMajor);

      console.log('Goal minor:', goalMinor);

      if (!goalMinor || goalMinor <= 0) {
        if (remainingBox) {
          remainingBox.style.display = 'none';
        }

        if (eligibleBox) {
          eligibleBox.style.display = 'none';
        }

        this.animateProgressBar(0);
        return;
      }

      const eligible = cartTotal >= goalMinor;
      const remaining = Math.max(0, goalMinor - cartTotal);

      // Remaining amount
      if (remainingStrong && !eligible) {
        remainingStrong.innerHTML = formatCents(remaining);
      }

      // Toggle messages
      if (remainingBox) {
        remainingBox.style.display = eligible ? 'none' : 'block';
      }

      if (eligibleBox) {
        eligibleBox.style.display = eligible ? 'block' : 'none';
      }

      // Progress
      const ratio = eligible
        ? 1
        : Math.min(1, cartTotal / goalMinor);

      this.animateProgressBar(ratio);
    }

    animateProgressBar(ratio) {
      const target = Math.max(0, Math.min(100, ratio * 100));
      const start  = this._currentPercent;
      if (start === target) {
        this.style.setProperty('--percent', `${target}%`);
        return;
      }

      const duration = 200; // ms
      const t0 = performance.now();
      const ease = (t) => t * (2 - t); // easeOutQuad

      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const val = start + (target - start) * ease(p);
        this._currentPercent = val;
        this.style.setProperty('--percent', `${val}%`);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }

  customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
}
